import { useState, useEffect, useCallback } from 'react';
import { useRoute, useLocation } from 'wouter';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  ExternalLink,
  Eye,
} from 'lucide-react';
import { useToast } from '@/components/ux/Toast';
import {
  useAdminLandingVariants,
  useCreateLandingVariant,
  useUpdateLandingVariant,
} from '@/lib/api/useLandingVariant';
import type {
  LandingVariantConfig,
  LandingVariantFeature,
  LandingVariantTestimonial,
  LandingVariantProfile,
  LandingVariantPress,
  LandingVariantFaq,
} from '@shared/types/landingVariant';
import { CreateLandingVariantSchema, UpdateLandingVariantSchema } from '@shared/types/landingVariant';
import { Loader } from '@/components/Loader';

// ── Defaults for new items ──────────────────────────────────────────────────

const emptyFeature: LandingVariantFeature = { title: '', description: '' };
const emptyTestimonial: LandingVariantTestimonial = { quote: '', name: '', location: '', verified: true };
const emptyProfile: LandingVariantProfile = { name: '', age: 25, zodiac: '', bio: '', image: '' };
const emptyPress: LandingVariantPress = { outlet: 'timeout', quote: '', url: '' };
const emptyFaq: LandingVariantFaq = { question: '', answer: '' };

const defaultConfig: LandingVariantConfig = {
  posthogEvent: '',
  hero: { glassBar: '', headline: '', subheadline: '', pillLeft: '', pillRight: '', ctaText: '', avatarImage: '' },
  features: [{ ...emptyFeature }, { ...emptyFeature }, { ...emptyFeature }],
  testimonials: [{ ...emptyTestimonial }],
  profiles: [{ ...emptyProfile }],
  press: [],
  faq: [{ ...emptyFaq }],
  howItWorksCompact: false,
  appStoreSource: '',
};

// ── Reusable form elements ──────────────────────────────────────────────────

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-neutral-400 mt-1">{hint}</p>}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 outline-none transition-colors ${props.className || ''}`}
    />
  );
}

function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`w-full px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 outline-none transition-colors resize-y ${props.className || ''}`}
    />
  );
}

function Section({ title, defaultOpen, children, count }: { title: string; defaultOpen?: boolean; children: React.ReactNode; count?: number }) {
  const [open, setOpen] = useState(defaultOpen ?? true);
  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden bg-white dark:bg-neutral-900">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-neutral-50 dark:bg-neutral-800/60 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-left"
      >
        <span className="font-semibold text-sm text-neutral-800 dark:text-neutral-200">
          {title}
          {count !== undefined && <span className="ml-2 text-xs font-normal text-neutral-400">({count})</span>}
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
      </button>
      {open && <div className="p-5 space-y-4 bg-white dark:bg-neutral-900">{children}</div>}
    </div>
  );
}

function ArrayItemWrapper({ index, onRemove, canRemove, children }: { index: number; onRemove: () => void; canRemove: boolean; children: React.ReactNode }) {
  return (
    <div className="relative border border-neutral-100 dark:border-neutral-700/50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-neutral-400">#{index + 1}</span>
        {canRemove && (
          <button type="button" onClick={onRemove} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function AddButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <Plus className="w-4 h-4" /> {label}
    </button>
  );
}

// ── Main Editor ─────────────────────────────────────────────────────────────

export default function LandingVariantEditor() {
  const [, params] = useRoute('/landing-variants/:id');
  const [, navigate] = useLocation();
  const toast = useToast();

  const isNew = params?.id === 'new';
  const variantId = isNew ? null : params?.id ?? null;

  const { data: listData, isLoading: listLoading } = useAdminLandingVariants();
  const createMutation = useCreateLandingVariant();
  const updateMutation = useUpdateLandingVariant();

  const existing = variantId ? listData?.data?.find((v) => v.id === variantId) : null;

  // Form state
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [language, setLanguage] = useState('en');
  const [isActive, setIsActive] = useState(true);
  const [config, setConfig] = useState<LandingVariantConfig>(structuredClone(defaultConfig));
  const [errors, setErrors] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (isNew && !initialized) {
      setInitialized(true);
      return;
    }
    if (existing && !initialized) {
      setName(existing.name);
      setSlug(existing.slug);
      setLanguage(existing.language);
      setIsActive(existing.isActive);
      setConfig(structuredClone(existing.config));
      setInitialized(true);
    }
  }, [existing, isNew, initialized]);

  // Config updaters
  const updateHero = useCallback((field: string, value: string) => {
    setConfig((c) => ({ ...c, hero: { ...c.hero, [field]: value } }));
  }, []);

  const updateFeature = useCallback((idx: number, field: keyof LandingVariantFeature, value: string) => {
    setConfig((c) => {
      const features = [...c.features];
      features[idx] = { ...features[idx], [field]: value };
      return { ...c, features };
    });
  }, []);

  const updateTestimonial = useCallback((idx: number, field: string, value: string | boolean) => {
    setConfig((c) => {
      const testimonials = [...c.testimonials];
      testimonials[idx] = { ...testimonials[idx], [field]: value };
      return { ...c, testimonials };
    });
  }, []);

  const updateProfile = useCallback((idx: number, field: string, value: string | number) => {
    setConfig((c) => {
      const profiles = [...c.profiles];
      profiles[idx] = { ...profiles[idx], [field]: value };
      return { ...c, profiles };
    });
  }, []);

  const updatePress = useCallback((idx: number, field: string, value: string) => {
    setConfig((c) => {
      const press = [...c.press];
      press[idx] = { ...press[idx], [field]: value } as LandingVariantPress;
      return { ...c, press };
    });
  }, []);

  const updateFaq = useCallback((idx: number, field: string, value: string) => {
    setConfig((c) => {
      const faq = [...c.faq];
      faq[idx] = { ...faq[idx], [field]: value };
      return { ...c, faq };
    });
  }, []);

  const removeFromArray = useCallback((key: 'testimonials' | 'profiles' | 'press' | 'faq', idx: number) => {
    setConfig((c) => ({ ...c, [key]: (c[key] as unknown[]).filter((_, i) => i !== idx) }));
  }, []);

  const addToArray = useCallback((key: 'testimonials' | 'profiles' | 'press' | 'faq') => {
    const defaults = { testimonials: emptyTestimonial, profiles: emptyProfile, press: emptyPress, faq: emptyFaq };
    setConfig((c) => ({ ...c, [key]: [...(c[key] as unknown[]), structuredClone(defaults[key])] }));
  }, []);

  // Save
  const handleSave = async () => {
    setErrors([]);
    const payload = { slug, name, language, isActive, config };

    const schema = isNew ? CreateLandingVariantSchema : UpdateLandingVariantSchema;
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      const flat = parsed.error.flatten();
      const msgs = [
        ...Object.entries(flat.fieldErrors).map(([k, v]) => `${k}: ${(v as string[]).join(', ')}`),
        ...flat.formErrors,
      ];
      setErrors(msgs);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      if (isNew) {
        const result = await createMutation.mutateAsync(payload as any);
        toast.showToast('Variant created', 'success');
        navigate(`/landing-variants/${result.data?.id}`, { replace: true });
      } else {
        await updateMutation.mutateAsync({ id: variantId!, ...payload } as any);
        toast.showToast('Variant saved', 'success');
      }
    } catch (e: any) {
      const msg = e?.message || 'Failed to save variant';
      toast.showToast(msg, 'error');
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (!isNew && listLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader />
      </div>
    );
  }

  if (!isNew && !existing && initialized) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-bold dark:text-neutral-100 mb-2">Variant not found</h2>
        <button onClick={() => navigate('/landing-variants')} className="text-primary-500 hover:underline text-sm">Back to list</button>
      </div>
    );
  }

  const previewSlug = slug;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/landing-variants')} className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
            <ArrowLeft className="w-5 h-5 text-neutral-500" />
          </button>
          <h1 className="text-2xl font-bold dark:text-neutral-100">{isNew ? 'Create Variant' : `Edit: ${existing?.name ?? ''}`}</h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <a
              href={`https://outclose.com/?v=${previewSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4" /> {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Validation errors */}
      {errors.length > 0 && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">Validation errors:</p>
          <ul className="text-sm text-red-600 dark:text-red-400 list-disc list-inside">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="space-y-5">
        {/* ── Basic Info ──────────────────────────────────────────── */}
        <Section title="Basic Info" defaultOpen>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Gay Men Landing" />
            </Field>
            <Field label="Slug" hint="Used as ?v= query parameter, e.g. outclose.com/?v=gay">
              <div className="flex gap-2">
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="gay" />
                {previewSlug && (
                  <a
                    href={`https://outclose.com/?v=${previewSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 shrink-0 rounded-lg border border-neutral-200 dark:border-neutral-700 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </a>
                )}
              </div>
            </Field>
            <Field label="Language">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 outline-none"
              >
                <option value="en">English</option>
                <option value="de">Deutsch</option>
                <option value="fr">Français</option>
                <option value="es">Español</option>
              </select>
            </Field>
            <Field label="Status">
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${isActive ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' : 'border-neutral-200 dark:border-neutral-700 text-neutral-500'}`}
              >
                <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-neutral-300'}`} />
                {isActive ? 'Active' : 'Inactive'}
              </button>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="PostHog Event">
              <Input value={config.posthogEvent} onChange={(e) => setConfig((c) => ({ ...c, posthogEvent: e.target.value }))} placeholder="landing_gay_viewed" />
            </Field>
            <Field label="App Store Source">
              <Input value={config.appStoreSource} onChange={(e) => setConfig((c) => ({ ...c, appStoreSource: e.target.value }))} placeholder="web_gay" />
            </Field>
          </div>
          <Field label="How It Works">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={config.howItWorksCompact}
                onChange={(e) => setConfig((c) => ({ ...c, howItWorksCompact: e.target.checked }))}
                className="rounded border-neutral-300 dark:border-neutral-600 text-primary-500 focus:ring-primary-500/40"
              />
              <span className="text-neutral-700 dark:text-neutral-300">Compact "How It Works" layout</span>
            </label>
          </Field>
        </Section>

        {/* ── Hero ────────────────────────────────────────────────── */}
        <Section title="Hero Section" defaultOpen>
          <Field label="Glass Bar">
            <Input value={config.hero.glassBar} onChange={(e) => updateHero('glassBar', e.target.value)} placeholder="Join 2,000+ members" />
          </Field>
          <Field label="Headline">
            <Input value={config.hero.headline} onChange={(e) => updateHero('headline', e.target.value)} placeholder="The dinner party for gay men" />
          </Field>
          <Field label="Subheadline">
            <Textarea rows={2} value={config.hero.subheadline} onChange={(e) => updateHero('subheadline', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Pill Left">
              <Input value={config.hero.pillLeft} onChange={(e) => updateHero('pillLeft', e.target.value)} />
            </Field>
            <Field label="Pill Right">
              <Input value={config.hero.pillRight} onChange={(e) => updateHero('pillRight', e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="CTA Text">
              <Input value={config.hero.ctaText} onChange={(e) => updateHero('ctaText', e.target.value)} placeholder="Join the table" />
            </Field>
            <Field label="Avatar Image" hint="Filename in /assets/images/">
              <Input value={config.hero.avatarImage} onChange={(e) => updateHero('avatarImage', e.target.value)} placeholder="hero-gay.webp" />
            </Field>
          </div>
        </Section>

        {/* ── Features (always 3) ────────────────────────────────── */}
        <Section title="Features" count={3}>
          <div className="space-y-4">
            {config.features.map((f, i) => (
              <div key={i} className="border border-neutral-100 dark:border-neutral-700/50 rounded-lg p-4 space-y-3">
                <span className="text-xs font-medium text-neutral-400">Feature #{i + 1}</span>
                <Field label="Title">
                  <Input value={f.title} onChange={(e) => updateFeature(i, 'title', e.target.value)} />
                </Field>
                <Field label="Description">
                  <Textarea rows={2} value={f.description} onChange={(e) => updateFeature(i, 'description', e.target.value)} />
                </Field>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Testimonials (1-6) ─────────────────────────────────── */}
        <Section title="Testimonials" count={config.testimonials.length} defaultOpen={false}>
          <div className="space-y-4">
            {config.testimonials.map((t, i) => (
              <ArrayItemWrapper key={i} index={i} canRemove={config.testimonials.length > 1} onRemove={() => removeFromArray('testimonials', i)}>
                <Field label="Quote">
                  <Textarea rows={2} value={t.quote} onChange={(e) => updateTestimonial(i, 'quote', e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name">
                    <Input value={t.name} onChange={(e) => updateTestimonial(i, 'name', e.target.value)} />
                  </Field>
                  <Field label="Location">
                    <Input value={t.location} onChange={(e) => updateTestimonial(i, 'location', e.target.value)} />
                  </Field>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={t.verified}
                    onChange={(e) => updateTestimonial(i, 'verified', e.target.checked)}
                    className="rounded border-neutral-300 dark:border-neutral-600 text-primary-500 focus:ring-primary-500/40"
                  />
                  <span className="text-neutral-700 dark:text-neutral-300">Verified</span>
                </label>
              </ArrayItemWrapper>
            ))}
          </div>
          <AddButton label="Add testimonial" onClick={() => addToArray('testimonials')} disabled={config.testimonials.length >= 6} />
        </Section>

        {/* ── Profiles (1-5) ─────────────────────────────────────── */}
        <Section title="Profiles" count={config.profiles.length} defaultOpen={false}>
          <div className="space-y-4">
            {config.profiles.map((p, i) => (
              <ArrayItemWrapper key={i} index={i} canRemove={config.profiles.length > 1} onRemove={() => removeFromArray('profiles', i)}>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Name">
                    <Input value={p.name} onChange={(e) => updateProfile(i, 'name', e.target.value)} />
                  </Field>
                  <Field label="Age">
                    <Input type="number" value={p.age} onChange={(e) => updateProfile(i, 'age', parseInt(e.target.value) || 0)} />
                  </Field>
                  <Field label="Zodiac">
                    <Input value={p.zodiac} onChange={(e) => updateProfile(i, 'zodiac', e.target.value)} />
                  </Field>
                </div>
                <Field label="Bio">
                  <Textarea rows={2} value={p.bio} onChange={(e) => updateProfile(i, 'bio', e.target.value)} />
                </Field>
                <Field label="Image" hint="Filename in /assets/images/">
                  <Input value={p.image} onChange={(e) => updateProfile(i, 'image', e.target.value)} />
                </Field>
              </ArrayItemWrapper>
            ))}
          </div>
          <AddButton label="Add profile" onClick={() => addToArray('profiles')} disabled={config.profiles.length >= 5} />
        </Section>

        {/* ── Press (0-4) ────────────────────────────────────────── */}
        <Section title="Press" count={config.press.length} defaultOpen={false}>
          <div className="space-y-4">
            {config.press.map((p, i) => (
              <ArrayItemWrapper key={i} index={i} canRemove onRemove={() => removeFromArray('press', i)}>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Outlet">
                    <select
                      value={p.outlet}
                      onChange={(e) => updatePress(i, 'outlet', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 outline-none"
                    >
                      <option value="timeout">Time Out</option>
                      <option value="them">Them</option>
                    </select>
                  </Field>
                  <Field label="URL">
                    <Input value={p.url} onChange={(e) => updatePress(i, 'url', e.target.value)} placeholder="https://..." />
                  </Field>
                </div>
                <Field label="Quote">
                  <Textarea rows={2} value={p.quote} onChange={(e) => updatePress(i, 'quote', e.target.value)} />
                </Field>
              </ArrayItemWrapper>
            ))}
          </div>
          <AddButton label="Add press item" onClick={() => addToArray('press')} disabled={config.press.length >= 4} />
        </Section>

        {/* ── FAQ (1-10) ─────────────────────────────────────────── */}
        <Section title="FAQ" count={config.faq.length} defaultOpen={false}>
          <div className="space-y-4">
            {config.faq.map((f, i) => (
              <ArrayItemWrapper key={i} index={i} canRemove={config.faq.length > 1} onRemove={() => removeFromArray('faq', i)}>
                <Field label="Question">
                  <Input value={f.question} onChange={(e) => updateFaq(i, 'question', e.target.value)} />
                </Field>
                <Field label="Answer">
                  <Textarea rows={2} value={f.answer} onChange={(e) => updateFaq(i, 'answer', e.target.value)} />
                </Field>
              </ArrayItemWrapper>
            ))}
          </div>
          <AddButton label="Add FAQ" onClick={() => addToArray('faq')} disabled={config.faq.length >= 10} />
        </Section>
      </div>

      {/* Sticky bottom save bar */}
      <div className="sticky bottom-0 mt-8 -mx-6 px-6 py-4 bg-white/80 dark:bg-neutral-900/80 backdrop-blur border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
        <button
          onClick={() => navigate('/landing-variants')}
          className="text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4" /> {isSaving ? 'Saving...' : isNew ? 'Create Variant' : 'Save Changes'}
        </button>
      </div>
    </motion.div>
  );
}
